const DB_NAME = "PDFReaderDB";
const DB_VERSION = 2; // 增加版本以支援 outlines

export interface Annotation {
  id: string;
  pageNumber: number;
  type: "highlight";
  color: "red" | "yellow" | "green" | "blue";
  opacity: 100 | 30;
  x: number;
  y: number;
  width: number;
  height: number;
  note?: string;
  pdfUrl: string;
  currentScreen: number;
}

export interface Bookmark {
  id: string;
  pageNumber: number;
  note?: string;
  color?: "red" | "yellow" | "green" | "blue";
  createdAt: number;
  pdfUrl: string;
}

export interface PDFOutline {
  pdfUrl: string;
  items: Array<{
    title: string;
    pageNumber: number;
  }>;
  updatedAt: number;
}

const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains("annotations")) {
        const annotationStore = db.createObjectStore("annotations", {
          keyPath: "id",
        });
        annotationStore.createIndex("pdfUrl", "pdfUrl", { unique: false });
        annotationStore.createIndex("pageNumber", "pageNumber", {
          unique: false,
        });
      }

      if (!db.objectStoreNames.contains("bookmarks")) {
        const bookmarkStore = db.createObjectStore("bookmarks", {
          keyPath: "id",
        });
        bookmarkStore.createIndex("pdfUrl", "pdfUrl", { unique: false });
        bookmarkStore.createIndex("pageNumber", "pageNumber", {
          unique: false,
        });
      }

      if (!db.objectStoreNames.contains("outlines")) {
        const outlineStore = db.createObjectStore("outlines", {
          keyPath: "pdfUrl",
        });
      }
    };
  });
};

// Annotations
export const saveAnnotation = async (annotation: Annotation): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(["annotations"], "readwrite");
  const store = transaction.objectStore("annotations");
  store.put(annotation);
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getAnnotations = async (pdfUrl: string): Promise<Annotation[]> => {
  const db = await initDB();
  const transaction = db.transaction(["annotations"], "readonly");
  const store = transaction.objectStore("annotations");
  const index = store.index("pdfUrl");
  const request = index.getAll(pdfUrl);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const deleteAnnotation = async (id: string): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(["annotations"], "readwrite");
  const store = transaction.objectStore("annotations");
  store.delete(id);
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// Bookmarks
export const saveBookmark = async (bookmark: Bookmark): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(["bookmarks"], "readwrite");
  const store = transaction.objectStore("bookmarks");
  store.put(bookmark);
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getBookmarks = async (pdfUrl: string): Promise<Bookmark[]> => {
  const db = await initDB();
  const transaction = db.transaction(["bookmarks"], "readonly");
  const store = transaction.objectStore("bookmarks");
  const index = store.index("pdfUrl");
  const request = index.getAll(pdfUrl);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const deleteBookmark = async (id: string): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(["bookmarks"], "readwrite");
  const store = transaction.objectStore("bookmarks");
  store.delete(id);
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// PDF Outlines (目錄)
export const saveOutline = async (outline: PDFOutline): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(["outlines"], "readwrite");
  const store = transaction.objectStore("outlines");
  store.put(outline);
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getOutline = async (pdfUrl: string): Promise<PDFOutline | null> => {
  const db = await initDB();
  const transaction = db.transaction(["outlines"], "readonly");
  const store = transaction.objectStore("outlines");
  const request = store.get(pdfUrl);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

export const deleteOutline = async (pdfUrl: string): Promise<void> => {
  const db = await initDB();
  const transaction = db.transaction(["outlines"], "readwrite");
  const store = transaction.objectStore("outlines");
  store.delete(pdfUrl);
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};
